import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { NOTIFICATION_MESSAGING } from "../../GraphQL/partnerOverlay";
import { IFRAME_CLIP_REQUEST } from "../../GraphQL/singleClip";
import { Iframe } from "../../Model/Iframe";
import { PartnerViewSession, IsUserLoggedIn } from "MVVM/State";
import {
  UniqueIdentifierState,
  ViewLocationState,
  ViewSubLocationState,
  NotificationBannerCopyType,
} from "MVVM/State/iframe";
import { getQueryParam } from "../../Utilities/QueryParam";
import { KNOWN, UNKNOWN, UNKNOWN_NMC } from "./configs";
import { RESTORE_PRUNED_CLIP } from "../../GraphQL/restorePrunedClip";
import {
  buildLink,
  checkIfClipOwner,
  getBestClaimableClip,
  getClaimableClipThumbnail,
  getPartnerAnalyticsId,
  getUserDisplayName,
  isLegacyPartner,
} from "./Utilities";

export const IframeViewModel = (
  clipId: any,
  useCase: any,
  platform: any,
  mid: any,
  pid: any,
) => {
  const [uniqueIdentifier, setUniqueIdentifier] = useRecoilState(
    UniqueIdentifierState,
  );
  const setViewLocation = useSetRecoilState(ViewLocationState);
  const setViewSubLocation = useSetRecoilState(ViewSubLocationState);
  const setNotificationBannerCopyType = useSetRecoilState(
    NotificationBannerCopyType,
  );

  useEffect(() => {
    const uid = getQueryParam("UID") ?? getQueryParam("uid") ?? "";
    setUniqueIdentifier(uid);
  }, [setUniqueIdentifier]);

  useEffect(() => {
    const location = getQueryParam("location") ?? "";
    setViewLocation(location);
  }, [setViewLocation]);

  useEffect(() => {
    const subLocation = getQueryParam("sublocation") ?? "";
    setViewSubLocation(subLocation);
  }, [setViewSubLocation]);

  const [partnerViewSession, setPartnerViewSession] =
    useRecoilState(PartnerViewSession);
  const defaultOverlayConfig = {
    display: false,
    knownUser: false,
    shareId: "",
    resconfig: "",
    fullscreen: false,
    uid: "",
    partner: {
      id: "",
      name: "",
      useCase: "",
      claimRoute: "",
    },
    remix: {
      show: false,
      link: ``,
    },
    share: {
      show: false,
      link: "",
      share: "",
      tiktokShare: "",
      youtubeShare: "",
      discordShare: "",
    },
    edit: {
      show: false,
      link: ``,
    },
    download: {
      show: false,
      link: ``,
    },
    mobile: {
      show: false,
      link: ``,
    },
    quality: {
      show: false,
      mobileLink: ``,
      remixLink: ``,
    },
    replay: { show: false },
    centerText: {
      show: false,
      content: ``,
    },
    button: {
      text: ``,
      link: ``,
      show: true,
    },
    attemptedAction: {
      adjustQuality: {
        actionPhrase: "partnerOverlay.adjustQuality.actionPhrase",
        button: "partnerOverlay.adjustQuality.button",
        link: buildLink({
          url: `${window.location.origin}/register`,
          game: partnerViewSession?.clip?.game,
          numClaimableClips: partnerViewSession?.claimableCount,
          username: getUserDisplayName(partnerViewSession),
          loggedInRedirect: `/remix/${clipId}`,
          platform: platform,
        }),
      },
      discord: {
        actionPhrase: "",
        button: "",
      },
      tiktok: {
        actionPhrase: "",
        button: "",
      },
      youtube: {
        actionPhrase: "",
        button: "",
      },
      download: {
        actionPhrase: "",
        button: "",
      },
      mobile: {
        actionPhrase: "",
        button: "",
      },
      addfx: {
        actionPhrase: "",
        button: "",
      },
      editTitle: {
        actionPhrase: "",
        button: "",
      },
    },
  };
  const ajs_src = getQueryParam("ajs_src");
  const steamIdQuery = getQueryParam("sid");
  const isCOTD = ajs_src === "cotd_gc";
  const adjustedUseCase = isCOTD ? "COTD" : useCase;
  const isOwnClip = isLegacyPartner(platform)
    ? ["PMH", "WC", "BP"].includes(adjustedUseCase) ||
      steamIdQuery === partnerViewSession?.clip?.steamId64 ||
      checkIfClipOwner(partnerViewSession?.clip, uniqueIdentifier)
    : checkIfClipOwner(partnerViewSession?.clip, uniqueIdentifier);

  let steamIdForClaim = getSteamIdForClaim({ isOwnClip, partnerViewSession });
  let puuidForClaim = partnerViewSession?.clip?.playerClipIdentifier;
  const iframe = Iframe.getInstance();
  const domain = window.location.origin;
  const { t } = useTranslation("translation", { keyPrefix: "partnerOverlay" });
  const [overlayConfig] = useState(defaultOverlayConfig);
  const isLoggedIn = useRecoilValue(IsUserLoggedIn);
  const knownUser = isLoggedIn || getQueryParam("known") === "true";
  const [resConfig, setResconfig] = useState("");

  const [requestNotificationData, { loading, data, error: requestError }] =
    useLazyQuery(NOTIFICATION_MESSAGING, {
      variables: {
        steamId: partnerViewSession.steamIdForClaim,
        puuid: partnerViewSession?.puuidForClaim,
        partner: platform,
      },

      onError(error) {
        setPartnerViewSession({
          ...partnerViewSession,
          fetchedSuggested: true,
        });
      },
      onCompleted: (data) => {
        //query is only made if we dont know the user.

        const messageData = data?.suggestedClips;
        const claimableCount = messageData?.clips?.length;
        const suggestedCount = messageData?.suggested?.length;

        if (!platform || partnerViewSession.fetchedSuggested) return;

        if (partnerViewSession?.overlayConfig?.knownUser) {
          setPartnerViewSession({
            ...partnerViewSession,
            dynamicCta: {
              buttonText: t("viewOnAllstar"),
              link: buildLink({
                url: `${domain}/clip?clip=${clipId}`,
                platform: platform,
              }),
              mainCopy: t("centerText", {
                partner:
                  partnerViewSession?.clip?.partner?.partnerId?.displayName,
              }),
              type: "view",
              isClaimable: false,
            },
          });
        } else if (claimableCount) {
          const bestClaimableClip = getBestClaimableClip(
            messageData?.clips,
            platform,
          );
          const bestId = bestClaimableClip?._id;
          setNotificationBannerCopyType("claimClips");
          setPartnerViewSession({
            ...partnerViewSession,
            fetchedSuggested: true,
            claimableCount: claimableCount,
            notificationBanner: {
              link: buildLink({
                url: `${domain}/register`,
                game: partnerViewSession?.clip?.game,
                numClaimableClips: claimableCount,
                username: getUserDisplayName(partnerViewSession),
                clipThumbnail: getClaimableClipThumbnail(bestClaimableClip),
                bestClipId: bestId,
                loggedInRedirect: `/clip?clip=${clipId}`,
                platform: platform,
              }),
              thumbnail: getClaimableClipThumbnail(bestClaimableClip),
              text: (
                <Trans
                  i18nKey="partnerOverlay.notificationBanner.claimClips"
                  values={{ count: claimableCount }}
                  defaults="You have <styledSpan>{{count}} unclaimed clips</styledSpan> on Allstar.gg."
                  components={{
                    styledSpan: <span>{claimableCount} unclaimed clips</span>,
                  }}
                />
              ),
              copy: `You have ${claimableCount} unclaimed clips on Allstar.gg`,
              buttonText: t(
                "partnerOverlay.notificationBanner.claimClips.buttonText",
                "Claim Clips",
              ),
            },
          });
        } else if (suggestedCount) {
          setPartnerViewSession({
            ...partnerViewSession,
            fetchedSuggested: true,
            claimableCount: suggestedCount,
            dynamicCta: {
              buttonText: `Make My ${suggestedCount} Clips`,
              link: buildLink({
                url: `${domain}/register`,
                game: partnerViewSession?.clip?.game,
                numClaimableClips: suggestedCount,
                username: getUserDisplayName(partnerViewSession),
                loggedInRedirect: `/clip?clip=${clipId}`,
                platform: platform,
              }),
              mainCopy: t("makeMyClips", { count: suggestedCount }),
              type: "suggested",
              isClaimable: true,
            },
          });
        } else if (
          partnerViewSession?.isOwnClip &&
          !partnerViewSession?.overlayConfig?.knownUser
        ) {
          setPartnerViewSession({
            ...partnerViewSession,
            dynamicCta: {
              buttonText: t("signupForAllstar"),
              link: buildLink({
                url: `${domain}/clip?clip=${clipId}`,
                platform: platform,
              }),
              mainCopy: t("centerText", {
                partner:
                  partnerViewSession?.clip?.partner?.partnerId?.displayName,
              }),
              type: "signup",
              isClaimable: false,
            },
          });
        }
      },
    });

  const [getClip, { loading: clipLoading, data: clipData, error: clipError }] =
    useLazyQuery(IFRAME_CLIP_REQUEST, { fetchPolicy: "network-only" });
  const [createPrunedClip] = useMutation(RESTORE_PRUNED_CLIP, {
    onError: (err: any) => {
      console.log(`mutateReaction error! ${err.message}`);
    },
  });

  useEffect(() => {
    setResconfig(getResConfig(partnerViewSession.clip));
    getOverlayConfig(partnerViewSession.clip);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    steamIdForClaim,
    puuidForClaim,
    partnerViewSession.clip,
    partnerViewSession?.claimableCount,
    partnerViewSession?.overlayConfig?.uid,
  ]);

  useEffect(() => {
    if (uniqueIdentifier) {
      //url decode the uid
      const decodedUid = decodeURIComponent(uniqueIdentifier);
      window.rudderanalytics.identify(decodedUid);
    }
  }, [uniqueIdentifier]);

  const getOverlayConfig = async (clip: TClip) => {
    if (!platform)
      return setPartnerViewSession({
        ...partnerViewSession,
        overlayConfig: {
          ...defaultOverlayConfig,
          fullscreen: getQueryParam("fullscreen") === "true" || false,
        },
        readyToFetchClip: true,
      });

    if (!getQueryParam("sid") && isOwnClip) {
      steamIdForClaim = partnerViewSession?.clip?.steamId64;
    }
    if (isOwnClip) {
      puuidForClaim = partnerViewSession?.clip?.playerGameIdentifier;
    }

    if (knownUser) {
      let known = KNOWN(
        clipId,
        getPartnerAnalyticsId(platform),
        partnerViewSession,
      );

      known.shareId = clipId;

      setPartnerViewSession({
        ...partnerViewSession,
        overlayConfig: {
          ...known,
          resconfig: resConfig,
          fullscreen: getQueryParam("fullscreen") === "true" || false,
          uid: uniqueIdentifier,
        },
        readyToFetchClip: true,
        steamIdForClaim: steamIdForClaim,
      });
    }
    if (!knownUser) {
      let unknownConfig = UNKNOWN(
        clipId,
        getPartnerAnalyticsId(platform),
        partnerViewSession,
        platform,
      );

      if (!isOwnClip)
        unknownConfig = UNKNOWN_NMC(
          clipId,
          getPartnerAnalyticsId(platform),
          partnerViewSession,
          platform,
        );
      unknownConfig.shareId = clipId;

      setPartnerViewSession({
        ...partnerViewSession,
        overlayConfig: {
          ...unknownConfig,
          resconfig: resConfig,
          fullscreen: getQueryParam("fullscreen") === "true" || false,
          uid: uniqueIdentifier,
        },

        readyToFetchClip: true,
        steamIdForClaim: steamIdForClaim,
        puuidForClaim: puuidForClaim,
        isOwnClip: !!isOwnClip,
      });
    }
  };

  const getPlaceholderData = async (resourceId: string) => {
    const resource = await iframe.getUnreadyResource(resourceId);

    if (resource) {
      return { ...resource, loading: false };
    } else {
      return { loading: false, error: true, status: 404 };
    }
  };

  const getSpectatedClip = async (
    platform: string,
    mid: string,
    pid: string,
  ) => {
    const resource = await iframe.getSpectatedClip(platform, mid, pid);

    if (resource) {
      return { ...resource, loading: false, status: 200 };
    } else {
      return { loading: false, error: true, status: 404 };
    }
  };

  return {
    overlayConfig: {
      ...overlayConfig,
      knownUser: knownUser,
      shareId: clipId,
    },
    notificationData: {
      requestNotificationData: requestNotificationData,
      error: requestError,
      data: data,
      loading: loading,
    },
    clip: {
      getClip: getClip,
      clipLoading: clipLoading,
      clipData: clipData,
      clipError: clipError,
      createPrunedClip: createPrunedClip,
    },
    getPlaceholderData: getPlaceholderData,
    getSpectatedClip: getSpectatedClip,
  };
};

const getSteamIdForClaim = ({ isOwnClip, partnerViewSession }: any) => {
  if (getQueryParam("sid") !== undefined) {
    return getQueryParam("sid");
  } else if (isOwnClip && partnerViewSession.clip?.steamId64) {
    return partnerViewSession.clip?.steamId64;
  } else return "";
};

const getResConfig = ({ clip }: any) => {
  const resolution = clip?.partner?.partnerConfigQuality?.qualitySetting;
  if (resolution === "720p60") return "2";
  else return "1";
};
